import React, { Component } from "react"
import PropTypes from "prop-types"
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  DropdownButton,
  MenuItem,
  Row,
  Table,
} from "react-bootstrap"
import { browserHistory, Link } from "react-router"
import {
  LoanStatus,
  fineractDateToStr,
  LoanCollateralEnum,
  LoanRecordingStep,
  fillingZeroUtility,
  getPlural,
  formatCurrency,
  getPercentAmount,
} from '../../lib/utils'
import ResubmitLoanConfirmation from "./ResubmitLoanConfirmation"
import AssignToForm from "./AssignToForm"
import FontAwesome from "react-fontawesome"
import AllDetails from "./AllDetails"
import apiServerApi from "../../redux/sagas/apiServerApi"
import { blockingFetchService } from "../../lib/blockingFetch"
import apiServerConstants from "../../redux/sagas/apiServerConstants"
import LoanDetailSummary from "./LoanDetailSummary"
import DisplayBundleModal from "./DisplayBundleModal"
import ProcessesStatus from "../fragments/ProcessesStatus"
import AlertModal from "../fragments/AlertModal"
import UpdateLoanBundle from "./UpdateLoanBundle"
import CustomBreadCrumb from "../fragments/CustomBreadCrumb"
import UIAvatar from "../fragments/UIAvatar"
import UpdateLoanGroupForm from "./UpdateLoanGroupForm"

class LoanForm extends Component {

  isApprover = this.props.isApprover
  isUnderwriter = this.props.isUnderwriter
  isAdmin = this.props.isAdmin
  isAccountant = this.props.isAccountant
  isAdminOrApprOrUnder = this.isAdmin || this.isApprover || this.isUnderwriter
  isImpersonation = this.props.isImpersonation
  isPaymentManager = this.props.isPaymentManager
  canEditDevice = this.isAdmin || this.isPaymentManager
  userCanOpenDevice = this.props.userCanOpenDevice

  constructor(props) {

    super(props)

    this.state = {
      showResubmit: false,
      loanImages: this.getImageGallery(),
      loanRecordingRunning: false,
      loanRecordingSuccess: false,
      loanRecordingError: null,
      displayBundle: false,
      waitForRecording: false,
      waitingTime: 15,
  
      openDeviceRunning: false,
      openDeviceSuccess: false,
      openDeviceError: null,
      showOpenDevice: true,
      showRepairZone: true,

      showDeleteConfirmation: false,
  
      running: false,
      success: false,
      error: null,
    }
  }

  componentDidMount() {
    const { loan } = this.props
    this.loadLoanDetails(loan.id)
  }

  componentWillUnmount() {
    clearTimeout(this.manageWaitingId)
  }

  willResubmitForApproval = status => this.isAdmin
    && (status === LoanStatus.UNDERWRITER_DECLINED || status === LoanStatus.DECLINED)

  loadLoanDetails = loanId => {
    if(this.canUserMakeRepayment()) {
      this.props.loadLoanDetails(loanId)
    }
  }

  getImageGallery = () => {

    const gallery = [],
      { loan } = this.props

    if(loan) {

      const { images } = loan
      if(images && images instanceof Array) {

        const thumbnailWidth = 180,
          thumbnailHeight = 200,
          documents = loan.isBusiness
            ? (loan.business ? loan.business.documents : [])
            : (loan.customer ? loan.customer.documents : [])
        images.forEach(image => {

          const src = `${apiServerConstants.host}/image/download/url/` + image.id,
            item = documents.find(value => value.imageType === image.imageType),
            caption = item ? item.label : image.imageType

          gallery.push({
            src,
            thumbnail: src,
            caption,
            thumbnailCaption: caption,
            thumbnailWidth,
            thumbnailHeight
          })
        })
      }
    }

    return gallery
  }

  getResubmitStatus = status => status === LoanStatus.UNDERWRITER_DECLINED
    ? LoanStatus.UNDERWRITER_REVIEW
    : LoanStatus.APPROVER_REVIEW

  /**
   * Re-trigger Loan Recording
   */
  resumeLoanRecording = (loanId, step) => {

    this.setState({
      loanRecordingRunning: true,
      loanRecordingSuccess: false,
      loanRecordingError: null,
    })

    blockingFetchService(
      accessToken => step === LoanRecordingStep.BEGIN
        ? apiServerApi.triggerLoanRecording(accessToken, loanId)
        : apiServerApi.resumeLoanRecording(accessToken, loanId, step),
      _ => {
        this.setState({ loanRecordingSuccess: true, waitForRecording: true, waitingTime: 15 })
        this.manageWaitBeforeLoading(loanId)
      },
      error => this.setState({ loanRecordingError: error.text }),
      _ => this.setState({ loanRecordingRunning: false })
    )
  }

  manageWaitBeforeLoading = loanId => {
    this.manageWaitingId = setTimeout(() => {

      let currentValue = this.state.waitingTime
      if(currentValue > 0) {
        this.setState({ waitingTime: --currentValue })
        this.manageWaitBeforeLoading(loanId)
      }
      else {
        this.setState({ waitForRecording: false })
        this.props.reloadLoan(loanId)
        this.loadLoanDetails(loanId)
      }
    }, 1000)
  }

  updateLoanBundleSuccess = ({ id, fineractId }) => {
    if(fineractId) {
      this.setState({ waitForRecording: true, waitingTime: 15 })
      this.manageWaitBeforeLoading(id)
    }
    else {
      this.props.reloadLoan(id)
    }
  }

  doOpenDevice = loanId => {

    this.setState({
      openDeviceRunning: true,
      openDeviceSuccess: false,
      openDeviceError: null,
    })

    blockingFetchService(
      accessToken => apiServerApi.openDevice(accessToken, loanId),
      _ => this.setState({ openDeviceSuccess: true }),
      error => this.setState({ openDeviceError: error.text }),
      _ => this.setState({ openDeviceRunning: false })
    )
  }

  doDeleteLoan = loanId => {

    this.setState({
      running: true,
      success: false,
      error: null,
    })

    blockingFetchService(
      accessToken => apiServerApi.deleteLoan(accessToken, loanId),
      _ => {
        this.setState({ success: true })
        setTimeout(() => browserHistory.push("/loans"), 1000)
      },
      error => this.setState({ error: error.text }),
      _ => this.setState({ running: false })
    )
  }

  confirmDeleteLoan = loanId => {
    this.setState({ showDeleteConfirmation: false })
    this.doDeleteLoan(loanId)
  }

  closeOpenDevice = () => this.setState({ showOpenDevice: false })

  canUserApprove = status => this.isAdminOrApprOrUnder 
    && (status === LoanStatus.APPROVER_REVIEW || status === LoanStatus.UNDERWRITER_REVIEW)

  canUserMakeRepayment = () => (this.isAdmin || this.isAccountant || this.isPaymentManager)
    && this.props.loan.status === LoanStatus.COMPLETED

  canUserAssignToHim = status => this.isAdminOrApprOrUnder
    && (status === LoanStatus.NEW || status === LoanStatus.UNDERWRITER_APPROVED)

  canUserDecline = status => this.isAdminOrApprOrUnder 
    && (status === LoanStatus.APPROVER_REVIEW || status === LoanStatus.UNDERWRITER_REVIEW)

  canUserDeleteLoan = () => this.isAdmin && this.props.loan.fineractId === 0

  canUserAssignTo = loanStatus => !!this.isAdmin
    && (loanStatus === LoanStatus.UNDERWRITER_REVIEW
    || loanStatus === LoanStatus.APPROVER_REVIEW
    || loanStatus === LoanStatus.UNDERWRITER_DECLINED
    || loanStatus === LoanStatus.DECLINED
    || loanStatus === LoanStatus.APPROVED
    || loanStatus === LoanStatus.COMPLETED)

  canUpdateLoanBundle = () => this.isImpersonation

  canChangeGroup = loanStatus => !!this.isAdmin && loanStatus === LoanStatus.COMPLETED

  closeRepairZone = () => this.setState({ showRepairZone: false })

  /**
   * Analyse loan details to get recording step
   */
  getRecordingStep = (loanFineractId, loanAmount, upfrontPayment, numberOfRepayments, imeis, loanDetails) => {

    let step = LoanRecordingStep.DONE
    try {

      if(loanFineractId === 0) {
        step = LoanRecordingStep.BEGIN
      }
      else if(!loanDetails) {
        step = LoanRecordingStep.DONE
      }
      else if(loanDetails.status.pendingApproval) {

        let imeiRegistered = true
        if(imeis && imeis.length) {
          imeiRegistered = false
          const notes = loanDetails.notes
          if(notes) {
            for(let i = 0; i < notes.length; ++i) {
              if(notes[i].note.indexOf('IMEI:') > -1) {
                imeiRegistered = true
                break
              }
            }
          }
        }

        if(!imeiRegistered) {
          step = LoanRecordingStep.ADD_LOAN_IMEI
        }
        else {
          const period1 = loanDetails.repaymentSchedule.periods[1],
            upfrontVal = getPercentAmount(loanAmount, upfrontPayment),
            amount = upfrontVal + ((loanAmount - upfrontVal)/numberOfRepayments)
          step = period1.principalDue !== amount
            ? LoanRecordingStep.UPDATE_FIRST_INSTALLMENT
            : LoanRecordingStep.APPROVE_LOAN
        }
      }
      else if(loanDetails.status.waitingForDisbursal) {
        step = LoanRecordingStep.DISBURSE_LOAN
      }
      else {
        let isUpfrontPaymentOK = true
        if(loanDetails.repaymentSchedule && upfrontPayment) {
          const totalRepayment = loanDetails.repaymentSchedule.totalRepayment,
            upfrontVal = getPercentAmount(loanAmount, upfrontPayment)
          isUpfrontPaymentOK = !!(totalRepayment && upfrontVal && totalRepayment >= upfrontVal)  
        }

        step = isUpfrontPaymentOK ? LoanRecordingStep.DONE : LoanRecordingStep.SAVE_UPFRONT_PAYMENT
      }
    }
    catch(e) { console.error(e) }

    return step
  }

  renderAssignTo = assignTo =>
    <p className="mt-40">
      <small>Assigned to:&nbsp;
        { assignTo 
        ? this.isAdmin 
          ? <Link to={`/users/${ assignTo.id }/edit`}>{ assignTo.name }</Link>
          : assignTo.name
        : '-'
        }
      </small>
    </p>

  renderLoanOverviewTd = (name, original, paid, outstanding, overDue) => (
    <tr>
      <th>{ name }</th>
      <td>{ formatCurrency(original) }</td>
      <td>{ formatCurrency(paid) }</td>
      <td>{ formatCurrency(outstanding) }</td>
      <td>{ formatCurrency(overDue) }</td>
    </tr>
  )

  renderLoanOverview = loanDetails => {

    const summary = loanDetails && loanDetails.summary ? loanDetails.summary : {},

    { principalDisbursed, principalPaid, principalOutstanding, principalOverdue,
      interestCharged, interestPaid, interestOutstanding, interestOverdue,
      feeChargesCharged, feeChargesPaid, feeChargesOutstanding, feeChargesOverdue,
      penaltyChargesCharged, penaltyChargesPaid, penaltyChargesOutstanding, penaltyChargesOverdue,
      totalExpectedRepayment, totalRepayment, totalOutstanding, totalOverdue 
    } = summary
  
    return (
      <Table responsive striped condensed hover>

        <thead>
          <tr>
            <th className="w-20p"></th>
            <th className="w-20p">Original</th>
            <th className="w-20p">Paid</th>
            <th className="w-20p">Outstanding</th>
            <th className="w-20p">Over Due</th>
          </tr>
        </thead>

        <tbody>
          { this.renderLoanOverviewTd('Principal', principalDisbursed, principalPaid, principalOutstanding, principalOverdue) }
          { this.renderLoanOverviewTd('Interest', interestCharged, interestPaid, interestOutstanding, interestOverdue) }
          { this.renderLoanOverviewTd('Fees', feeChargesCharged, feeChargesPaid, feeChargesOutstanding, feeChargesOverdue) }
          { this.renderLoanOverviewTd('Penalties', penaltyChargesCharged, penaltyChargesPaid, penaltyChargesOutstanding, penaltyChargesOverdue) }          
        </tbody>

        <tfoot>
          <tr>
            <th>Total</th>
            <th>{ formatCurrency(totalExpectedRepayment) }</th>
            <th>{ formatCurrency(totalRepayment) }</th>
            <th>{ formatCurrency(totalOutstanding) }</th>
            <th>{ formatCurrency(totalOverdue) }</th>
          </tr>
        </tfoot>
      </Table>
    )
  }

  renderMainInfo = (loan, loanDetails) => {

    const { bundle, bundleQty, clientName, clientPhoneNumber, assignTo, status, fineractId,
      amount, isDefaulted } = loan,

      loanSummary = loanDetails && loanDetails.summary,

      loanDelinquent = loanDetails && loanDetails.delinquent,

      isArrear = !!(loanDetails && loanDetails.inArrears),

      statusColor = (this.willResubmitForApproval(status) || isArrear)
      ? 'red'
      : status === LoanStatus.COMPLETED
        ? 'green'
        : '#444',

      currentBalance = loanSummary
      ? loanSummary.totalOutstanding
      : status === LoanStatus.COMPLETED 
        ? null
        : amount,

      inArrears = loanDetails
      ? loanDetails.inArrears
        ? 'Yes'
        : 'No'
      : '',

      inArrearsBy = loanDelinquent && loanDelinquent.delinquentAmount
        ? loanDelinquent.delinquentAmount : '',

      inArrearsSince = loanSummary && loanSummary.overdueSinceDate
        ? loanSummary.overdueSinceDate : '',

      inArrearsSinceStr = inArrearsSince ? fineractDateToStr(inArrearsSince) : ''

    return (
      <div>

        <div className="flex-row-simple justify-between items-center gap-10 flex-wrap sm:flex-nowrap">
          <h2 className={ statusColor + ' text-2xl sm:text-3xl' }>
            <FontAwesome name="square"/>&nbsp;
            { bundle.name }&nbsp;
            <small className="gray">(#{ bundle.shortName })</small>
          </h2>

          { isDefaulted &&
          <h4 className="red border-1 border-red rounded-10 p-10" title="Defaulted loan">
            Defaulted
          </h4>}
        </div>

        <h4 title="The bundle quantity">
          #Qty: <span className="bundle-qty">{ fillingZeroUtility(bundleQty) }</span>
        </h4>

        <h3 className="mt-40">
          <UIAvatar name={ clientName } size={ 28 } /> { clientName }
        </h3>

        <span className="main-phonenumber">
          <FontAwesome name='phone'/>&nbsp;
          { clientPhoneNumber }
        </span>

        <h4 className="mt-40">
          Current Balance: { currentBalance ? `${bundle.currencyCode} ${formatCurrency(currentBalance)}` : '_' }
        </h4>

        <p className="mv-20 flex gap-10 flex-wrap">

          <span>In Arrears: { inArrears }</span>

          { inArrearsBy && inArrearsSinceStr &&
          <span className="flex gap-10 flex-wrap">
            <span className="gray">|</span>
            { inArrearsBy && <span>Arrears By: { formatCurrency(inArrearsBy) }</span> }
            <span className="gray">|</span>
            { inArrearsSinceStr && <span>Arrears Since: { inArrearsSinceStr }</span> }
          </span>}
        </p>

        { fineractId > 0 && this.renderLoanOverview(loanDetails) }

        { this.renderAssignTo(assignTo) }
      </div>
    )
  }

  renderRepairZones = (loanId, loanFineractId, loanAmount, upfrontPayment, numberOfRepayments, imeis, loanDetails) => {

    const { loanRecordingSuccess, showRepairZone, loanRecordingError, loanRecordingRunning } = this.state
    if(!this.canUserMakeRepayment() || loanRecordingSuccess || !showRepairZone) return null

    const step = this.getRecordingStep(loanFineractId, loanAmount, upfrontPayment, numberOfRepayments, imeis, loanDetails)
    if(step === LoanRecordingStep.DONE) return null

    return (
      <Alert bsStyle="warning" onDismiss={ this.closeRepairZone }>
      { loanRecordingRunning 
        ? <span><FontAwesome name='clock-o'/> Running ..</span>
        : <span>
          An error occurred while recording the loan. { loanRecordingError } &nbsp;
          <span className="reloadSave" onClick={() => this.resumeLoanRecording(loanId, step)}>
            Please click to repair <FontAwesome name='refresh'/>
          </span>
        </span>
      }
      </Alert>
    )
  }

  renderStatuses = () => {

    const { running, success, error } = this.state
    return <ProcessesStatus
      running={ running }
      success={ success }
      error={ error }
      runningText={ <span><FontAwesome name='clock-o'/> Running ..</span> }
      successText={ <span><FontAwesome name='check'/> OK</span> }
    />
  }

  waitingRecording = () => {

    const { waitForRecording, waitingTime } = this.state
    if(!waitForRecording) return null
    return (
      <Alert bsStyle="warning">
        <span><FontAwesome name='clock-o'/> Please wait ..</span>
        <span> { waitingTime }</span>
      </Alert>
    )
  }

  renderOpenDevice = (canOpenDevice, loanId, bundleQty) => {

    if(!canOpenDevice) return null

    const { openDeviceRunning, openDeviceError } = this.state
    return (
      <Alert bsStyle="success" onDismiss={ this.closeOpenDevice }>
      { openDeviceRunning

      ? <span><FontAwesome name='clock-o'/> Running ..</span>

      : openDeviceError

        ? <span>An error occurred. { openDeviceError } &nbsp;
            <span className="reloadSave" onClick={() => this.doOpenDevice(loanId)}>
              Please click to retry <FontAwesome name='refresh'/>
            </span>
          </span>

        : <h3>
          <FontAwesome name='unlock'/> &nbsp;
          <span className="text-underline cursor-pointer" 
            title="Loan fully repaid. You can now open this device!"
            onClick={() => this.doOpenDevice(loanId)}>
            Open { fillingZeroUtility(bundleQty) } { getPlural('device', bundleQty) }
          </span>
        </h3>
      }
      </Alert>
    )
  }

  renderActionButtons = (loan, isLoanFullRepay, waitForRecording) => {

    const { approveLoanClick, assignLoanClick, declineLoanClick } = this.props,
    { id: loanId, status: loanStatus, isDefaulted } = loan

    return <ButtonGroup justified>
      <Button
        href="#"
        bsStyle="success"
        onClick={() => approveLoanClick(loanId, loanStatus)}
        disabled={ !this.canUserApprove(loanStatus) }>
        <FontAwesome name='check'/> Approve
      </Button>

      <Button
        href="#"
        bsStyle="primary"
        onClick={() => browserHistory.push(`/loans/${loanId}/repayment`)}
        disabled={ !this.canUserMakeRepayment() || isLoanFullRepay || isDefaulted }>
        <FontAwesome name='dollar'/> Make Repayment
      </Button>

      <DropdownButton title="More" id="bg-justified-dropdown">

        <MenuItem
          eventKey="1"
          onClick={() => {
            if(this.canUserAssignToHim(loanStatus)) {
              assignLoanClick(loanId, loanStatus)
            }
          }}
          disabled={ !this.canUserAssignToHim(loanStatus) }>
          Assign to Me
        </MenuItem>

        <MenuItem
          eventKey="2"
          onClick={() => {
            if(this.canUserDecline(loanStatus)) {
              declineLoanClick(loanId, loanStatus)
            }
          }}
          disabled={ !this.canUserDecline(loanStatus) }>
          <span className="red"><FontAwesome name='ban'/> Decline</span> 
        </MenuItem>

        { this.willResubmitForApproval(loanStatus) &&
          <MenuItem
            eventKey="3"
            onClick={() => this.setState({ showResubmit: true })}>
            Resubmit for approval
          </MenuItem>
        }

        { this.canUserDeleteLoan() &&
          <MenuItem
            eventKey="4"
            onClick={() => {
              if(!waitForRecording) {
                this.setState({ showDeleteConfirmation: true })
              }
            }}
            disabled={ waitForRecording }>
            <span className="hot-color"><FontAwesome name='trash'/> Delete Loan</span>
          </MenuItem>
        }

        { this.canUpdateLoanBundle() && 
          <UpdateLoanBundle
            loan={ loan }
            waitForRecording={ waitForRecording }
            onSuccess={() => this.updateLoanBundleSuccess(loan) }
          />
        }
      </DropdownButton>
    </ButtonGroup>
  }

  renderBundleLink = () => this.isPaymentManager ? null :
    <Link className="cursor-pointer ml-20" onClick={() => this.setState({ displayBundle: true })}>
      <FontAwesome name='suitcase'/> Bundle info
    </Link>

  render() {

    const { loanImages, openDeviceSuccess, showOpenDevice, showResubmit, displayBundle, 
      showDeleteConfirmation, waitForRecording } = this.state,

      { loan, loanDetails, resubmitDeclineLoanClick, detailsRunning, detailsFailed,
        loanCommissions, setLoan, reloadLoan } = this.props,

      { bundle, bundleQty, id: loanId, status: loanStatus, fineractId, amount, imeis, isBusiness,
        clientId, clientName, disburseDate, charges: loanCharges, deviceOpen } = loan,

      expectedMaturityDate = loanDetails ? fineractDateToStr(loanDetails.timeline.expectedMaturityDate) : '',
      actualDisbursementDate = loanDetails ? fineractDateToStr(loanDetails.timeline.actualDisbursementDate) : null,
      isLoanFullRepay = loanDetails && loanDetails.summary && loanDetails.summary.totalOutstanding === 0,

      canOpenDevice = this.userCanOpenDevice && isLoanFullRepay && !deviceOpen && !openDeviceSuccess
        && bundle.loanCollateral === LoanCollateralEnum.DEVICE && showOpenDevice,

      clientUrl = isBusiness ? `/businesses/${ clientId }/detail` : `/customers/${ clientId }/detail`,
      dataBreadCrumbs = [{ url: '/loans', title: 'Loans' }, { url: clientUrl, title: clientName }]

    return (
      <div>
        <CustomBreadCrumb activeText={ bundle.name } data={ dataBreadCrumbs } />

        <Row>

          <Col md={6}>
            { this.renderMainInfo(loan, loanDetails) }
          </Col>

          <Col md={6}>

            { this.renderStatuses() }
            { this.waitingRecording() }
            { this.renderRepairZones(loanId, fineractId, amount,
              bundle.upfrontPayment, bundle.numberOfRepayments, imeis, loanDetails) }
            { this.renderOpenDevice(canOpenDevice, loanId, bundleQty) }
            { this.renderActionButtons(loan, isLoanFullRepay, waitForRecording) }
            <br/>

            <ResubmitLoanConfirmation
              resubmitAction={() => resubmitDeclineLoanClick(loanId, this.getResubmitStatus(loanStatus))}
              showResubmit={ showResubmit }
              handleClose={() => this.setState({ showResubmit: false })}
            />

            <AlertModal
              show={ showDeleteConfirmation }
              title="Confirm deletion"
              body={ <h3>Are you sure you want to delete this loan?</h3> }
              bsStyle="danger"
              close={() => this.setState({ showDeleteConfirmation: false })}
              confirm={() => this.confirmDeleteLoan(loanId)}
              proceedText="Proceed"
            />

            <LoanDetailSummary
              loan={ loan }
              expectedMaturityDate={ expectedMaturityDate }
              actualDisbursementDate={ actualDisbursementDate }
              isAdmin={ this.isAdmin }
              isAccountant={ this.isAccountant }
              canEditDevice={ this.canEditDevice }
              reloadLoan={ reloadLoan }
            />

            <div>
              <Link to={ clientUrl }>
                <FontAwesome name={ isBusiness ? 'building-o' : 'user' } />
                &nbsp;{ isBusiness ? 'Business' : 'Customer' } info
              </Link>

              { this.renderBundleLink() }

              <DisplayBundleModal
                bundle={ bundle }
                loanId={ loanId }
                show={ displayBundle }
                close={() => this.setState({ displayBundle: false })}
                loanCommissions={ loanCommissions }
                setLoan={ setLoan }
              />
            </div>

            <table className="mt-20">
              <tbody>
              { this.canUserAssignTo(loanStatus) &&
                <tr>
                  <td>Assign To</td>
                  <td className="p-5">
                    <AssignToForm
                      loanStatus={ loanStatus }
                      selectedLoans={[ loan ]}
                      nbItems={ 1 }
                    />
                  </td>
                </tr>}

              { this.canChangeGroup(loanStatus) && !isLoanFullRepay &&
                <tr>
                  <td>Update group</td>
                  <td className="p-5">
                    <UpdateLoanGroupForm
                      selectedLoans={[ loan ]}
                      nbItems={ 1 }
                    />
                  </td>
                </tr>}
              </tbody>
            </table>
          </Col>

          <Col md={12}>
            <AllDetails
              isAuthorized={ this.canUserMakeRepayment() }
              loanDetails={ loanDetails }
              loanImages={ loanImages }
              detailsRunning={ detailsRunning }
              detailsFailed={ detailsFailed }
              reloadDetails={() => this.loadLoanDetails(loanId)}
              loanId={ loanId }
              disburseDate={ disburseDate }
              loanCharges={ loanCharges }
            />
          </Col>
        </Row>
      </div>
    )
  }
}

LoanForm.propTypes = {
  loan: PropTypes.object.isRequired,
  loadLoanDetails: PropTypes.func.isRequired,
  setLoan: PropTypes.func.isRequired,
  reloadLoan: PropTypes.func.isRequired,
}

export default LoanForm



// WEBPACK FOOTER //
// ./src/components/Loans/Form.js